// @ts-nocheck
// ============================================================
// TWIN STORE — Fase 10 + Fase 11 (Hardened)
// Zustand store para Digital Twin & Autonomous Operations
// ============================================================
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type {
  TwinState, Equipment, EventCorrelation, AutonomousAction,
  OperationalRecommendation, SimulationScenario, SimulationResult,
  SpatialRisk, ObraZone,
} from '@/types/twin'

// ─── Bounded circular buffer ──────────────────────────────
const bounded = <T>(arr: T[], item: T, max: number): T[] =>
  [item, ...arr].slice(0, max)

interface TwinStore {
  // ─── State ──────────────────────────────────────────────
  twinState:           TwinState | null
  correlations:        EventCorrelation[]
  autonomousActions:   AutonomousAction[]
  recommendations:     OperationalRecommendation[]
  simulations:         SimulationResult[]
  activeScenario:      SimulationScenario | null
  selectedZoneId:      string | null
  overallRiskScore:    number
  isLoading:           boolean
  lastUpdated:         string | null
  error:               string | null

  // ─── Actions ─────────────────────────────────────────────
  setTwinState:           (state: TwinState) => void
  updateEquipment:        (equipment: Equipment[]) => void
  updateSingleEquipment:  (eq: Equipment) => void          // FIX: realtime single update
  updateZones:            (zones: ObraZone[]) => void
  setRisks:               (risks: SpatialRisk[]) => void
  addCorrelation:         (c: EventCorrelation) => void
  setCorrelations:        (cs: EventCorrelation[]) => void
  addAutonomousAction:    (a: AutonomousAction) => void
  setRecommendations:     (rs: OperationalRecommendation[]) => void
  addSimulation:          (r: SimulationResult) => void
  setActiveScenario:      (s: SimulationScenario | null) => void
  setSelectedZone:        (id: string | null) => void
  setOverallRiskScore:    (score: number) => void
  setLoading:             (v: boolean) => void
  setError:               (e: string | null) => void
  reset:                  () => void
}

const INITIAL: Omit<TwinStore,
  | 'setTwinState' | 'updateEquipment' | 'updateSingleEquipment'
  | 'updateZones' | 'setRisks' | 'addCorrelation' | 'setCorrelations'
  | 'addAutonomousAction' | 'setRecommendations' | 'addSimulation'
  | 'setActiveScenario' | 'setSelectedZone' | 'setOverallRiskScore'
  | 'setLoading' | 'setError' | 'reset'
> = {
  twinState:         null,
  correlations:      [],
  autonomousActions: [],
  recommendations:   [],
  simulations:       [],
  activeScenario:    null,
  selectedZoneId:    null,
  overallRiskScore:  0,
  isLoading:         false,
  lastUpdated:       null,
  error:             null,
}

export const useTwinStore = create<TwinStore>()(
  subscribeWithSelector((set) => ({
    ...INITIAL,

    setTwinState: (state) => set({
      twinState:   state,
      lastUpdated: new Date().toISOString(),
      error:       null,
    }),

    updateEquipment: (equipment) => set((s) => ({
      twinState: s.twinState ? { ...s.twinState, equipment } : null,
    })),

    // FIX PROB-03: single equipment update from realtime payload
    updateSingleEquipment: (eq) => set((s) => {
      if (!s.twinState) return {}
      const equipment = s.twinState.equipment.map(e => e.id === eq.id ? eq : e)
      return { twinState: { ...s.twinState, equipment } }
    }),

    updateZones: (zones) => set((s) => ({
      twinState: s.twinState ? { ...s.twinState, zones } : null,
    })),

    setRisks: (risks) => set((s) => ({
      twinState: s.twinState ? { ...s.twinState, risks } : null,
    })),

    addCorrelation: (c) => set((s) => {
      // Dedup by event_types hash (FIX PROB-14)
      const key = [...c.event_types].sort().join('|')
      const exists = s.correlations.some(x =>
        x.id === c.id || [...x.event_types].sort().join('|') === key,
      )
      if (exists) return {}
      const correlations = bounded(s.correlations, c, 100)
      return {
        correlations,
        twinState: s.twinState ? { ...s.twinState, correlations } : null,
      }
    }),

    setCorrelations: (correlations) => set((s) => ({
      correlations,
      twinState: s.twinState ? { ...s.twinState, correlations } : null,
    })),

    addAutonomousAction: (a) => set((s) => ({
      autonomousActions: bounded(s.autonomousActions, a, 200),
    })),

    setRecommendations: (recommendations) => set({ recommendations }),

    addSimulation: (r) => set((s) => ({
      simulations: bounded(s.simulations, r, 50),
    })),

    setActiveScenario: (activeScenario) => set({ activeScenario }),

    setSelectedZone: (selectedZoneId) => set({ selectedZoneId }),

    setOverallRiskScore: (overallRiskScore) => set({ overallRiskScore }),

    setLoading: (isLoading) => set({ isLoading }),

    setError: (error) => set({ error }),

    reset: () => set(INITIAL),
  })),
)

// ─── Selectors (stable references) ───────────────────────
export const selectCriticalZones = (s: TwinStore) =>
  s.twinState?.zones.filter(z => z.risk_level === 'critical' || z.risk_level === 'emergency') ?? []

export const selectCriticalEquipment = (s: TwinStore) =>
  s.twinState?.equipment.filter(e => e.status === 'critical' || e.status === 'offline') ?? []

// FIX PROB-12: exclude propagated risks from active list
export const selectActiveRisks = (s: TwinStore) =>
  s.twinState?.risks.filter(r => r.is_active && !r.is_propagated) ?? []

export const selectAllActiveRisks = (s: TwinStore) =>
  s.twinState?.risks.filter(r => r.is_active) ?? []

export const selectActiveCorrelations = (s: TwinStore) =>
  s.correlations.filter(c => c.is_active)

export const selectHighPriorityRecs = (s: TwinStore) =>
  s.recommendations.filter(r => r.priority <= 2).slice(0, 5)

export const selectSelectedZone = (s: TwinStore) =>
  s.twinState?.zones.find(z => z.id === s.selectedZoneId) ?? null
